import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPlus,
  faTrash,
  faArrowUp,
  faArrowDown,
  faFileImport,
  faBan,
  faListCheck,
  faCopy,
  faArrowUpRightFromSquare,
  faBookmark,
  faPaperPlane,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import {
  DeployTest,
  DeployTestItem,
  DeployTestItemStatus,
  DeployTestLibraryItem,
  DeployTestQuestion,
  DeployTestQuestionType,
  DeployTestStatus,
  User,
} from '../../types';
import { API_BASE_URL } from '../../config/api';
import { markdownToHtml } from '../../utils/markdownHtml';
import MarkdownHelp from './MarkdownHelp';
import { BETA_PROGRAM, DeployProgram } from '../../config/deployProgram';
import {
  FirmwareOption,
  ITEM_STATUS_OPTIONS,
  MobileAppOption,
  SELECT_LATER,
  STATUS_OPTIONS,
  TestFormState,
  emptyForm,
  emptyItem,
  emptyQuestion,
  firmwareTypeLabel,
  formFromTest,
  QUESTION_TYPE_OPTIONS,
  resolveItemStatus,
} from './deployTestUtils';

interface DeployTestPageProps {
  program?: DeployProgram;
}

const parseEmailRecipients = (value: string) => (
  value
    .split(/[\s,;]+/)
    .map((email) => email.trim())
    .filter(Boolean)
);

const stripMarkdownListMarker = (value: string) => value.trim().replace(/^([-*+]\s+|\d+[.)]\s+)/, '').trim();

const DeployTestPage: React.FC<DeployTestPageProps> = ({ program = BETA_PROGRAM }) => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const isNew = !testId || testId === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState<TestFormState>(emptyForm);
  const [editingTest, setEditingTest] = useState<DeployTest | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [aborting, setAborting] = useState(false);
  const [reinstating, setReinstating] = useState(false);
  const [testers, setTesters] = useState<User[]>([]);
  const [testersLoading, setTestersLoading] = useState(false);
  const [taskLibrary, setTaskLibrary] = useState<DeployTestLibraryItem[]>([]);
  const [questionLibrary, setQuestionLibrary] = useState<DeployTestLibraryItem[]>([]);
  const [taskLibraryPickId, setTaskLibraryPickId] = useState('');
  const [questionLibraryPickId, setQuestionLibraryPickId] = useState('');
  const [savingLibraryKey, setSavingLibraryKey] = useState<string | null>(null);
  const [firmwareOptions, setFirmwareOptions] = useState<FirmwareOption[]>([]);
  const [mobileAppOptions, setMobileAppOptions] = useState<MobileAppOption[]>([]);
  const [copiedPublicUrl, setCopiedPublicUrl] = useState(false);
  const [previewEmailOpen, setPreviewEmailOpen] = useState(false);
  const [kickoffEmailSubject, setKickoffEmailSubject] = useState('');
  const [kickoffEmailRecipients, setKickoffEmailRecipients] = useState('');
  const [sendingKickoffEmail, setSendingKickoffEmail] = useState(false);
  const [kickoffEmailStatus, setKickoffEmailStatus] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
  const [showTesters, setShowTesters] = useState(false);

  const fetchOptions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}${program.testsApi}/options`, {
        withCredentials: true,
        params: { track: program.track },
      });
      setFirmwareOptions(response.data.firmware || []);
      setMobileAppOptions(response.data.mobileAppVersions || []);
    } catch (err) {
      console.error('Error fetching deploy test options:', err);
    }
  }, [program.testsApi, program.track]);

  const fetchTesters = useCallback(async () => {
    if (!['beta', 'internal'].includes(program.track)) return;
    setTestersLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/users`, {
        withCredentials: true,
        params: {
          tester: program.track,
          limit: 5000,
          page: 1,
          sortColumn: 'email',
        },
      });
      setTesters(response.data.users || []);
    } catch (err) {
      console.error('Error fetching testers:', err);
    } finally {
      setTestersLoading(false);
    }
  }, [program.track]);

  const fetchLibrary = useCallback(async () => {
    try {
      const [tasksResponse, questionsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}${program.testsApi}/library`, {
          withCredentials: true,
          params: { itemType: 'task' },
        }),
        axios.get(`${API_BASE_URL}${program.testsApi}/library`, {
          withCredentials: true,
          params: { itemType: 'question' },
        }),
      ]);
      setTaskLibrary(tasksResponse.data.items || []);
      setQuestionLibrary(questionsResponse.data.items || []);
    } catch (err) {
      console.error('Error fetching deploy test library:', err);
    }
  }, [program.testsApi]);

  const fetchTest = useCallback(async () => {
    if (isNew || !testId) {
      setEditingTest(null);
      setForm(emptyForm());
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}${program.testsApi}/${testId}`, {
        withCredentials: true,
      });
      const test = response.data.test as DeployTest;
      setEditingTest(test);
      const nextForm = formFromTest(test);
      setForm(nextForm);
      setShowTesters(nextForm.deployToAllTesters === false);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching deploy test:', err);
      setError(err.response?.data?.message || 'Failed to load deploy test');
      setEditingTest(null);
    } finally {
      setLoading(false);
    }
  }, [isNew, testId, program.testsApi]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    fetchTesters();
  }, [fetchTesters]);

  useEffect(() => {
    fetchLibrary();
  }, [fetchLibrary]);

  useEffect(() => {
    fetchTest();
  }, [fetchTest]);

  const uniqueFirmwareVersions = useMemo(() => {
    const seen = new Set<string>();
    return firmwareOptions.filter((option) => {
      const key = `${option.version}::${option.type || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [firmwareOptions]);

  const uniqueMobileAppVersionsByPlatform = useMemo(() => {
    const byPlatform: Record<'ios' | 'android', MobileAppOption[]> = {
      ios: [],
      android: [],
    };
    const seen = {
      ios: new Set<string>(),
      android: new Set<string>(),
    };
    mobileAppOptions.forEach((option) => {
      if (option.platform !== 'ios' && option.platform !== 'android') return;
      if (seen[option.platform].has(option.version)) return;
      seen[option.platform].add(option.version);
      byPlatform[option.platform].push(option);
    });
    return byPlatform;
  }, [mobileAppOptions]);

  const selectedFirmware = firmwareOptions.find((option) => option._id === form.firmwareReleaseId)
    || firmwareOptions.find((option) => option.version === form.firmwareVersion);
  const canEditVersions = form.status === 'planning' || form.status === 'aborted';
  const canImportReleaseNotes = form.status === 'planning' || form.status === 'testing';
  const isInternalProgram = program.track === 'internal';
  const iosEnabled = !isInternalProgram || form.mobileAppPlatforms.includes('ios');
  const androidEnabled = !isInternalProgram || form.mobileAppPlatforms.includes('android');
  const releaseNoteLines = form.releaseNotes.split('\n');
  const testerHasPlatform = (tester: User, platform: 'ios' | 'android') => (
    (tester.phoneDevices || []).some((device) => device.platform === platform)
  );
  const testerMatchesActivePlatforms = (tester: User) => (
    !isInternalProgram
    || form.mobileAppPlatforms.some((platform) => testerHasPlatform(tester, platform))
  );

  const kickoffEmailPreview = useMemo(() => {
    const body = form.kickoffEmail.trim() || '[Email intro]';
    const releaseNotes = form.releaseNotes
      .split('\n')
      .map(stripMarkdownListMarker)
      .filter(Boolean);
    const instructions = form.overview.trim() || '[Instructions]';
    const tasks = form.items
      .map((item) => {
        const title = item.text.trim();
        const description = (item.description || '').trim();
        if (!title && !description) return null;
        return {
          title: title || 'Untitled task',
          description,
        };
      })
      .filter(Boolean) as { title: string; description: string }[];
    const wikiUrl = program.track === 'beta'
      ? 'https://wiki.fluoraplant.com/beta'
      : 'https://wiki.fluoraplant.com/beta';
    const testName = form.name.trim() || 'Not specified';
    const testType = program.track === 'internal' ? 'Internal Test' : 'Beta Test';

    return {
      body,
      releaseNotes,
      instructions,
      tasks,
      wikiUrl,
      subject: `New ${testType} Ready (${testName})`,
      subjectPrefix: `New ${testType} Ready`,
      testName,
      firmwareVersion: form.firmwareVersion.trim() ? `v${form.firmwareVersion.trim().replace(/^v/i, '')}` : 'Not specified',
      mobileAppVersion: form.mobileAppVersion.trim() ? `v${form.mobileAppVersion.trim().replace(/^v/i, '')}` : 'Not specified',
      mobileAppVersionIos: form.mobileAppVersionIos.trim() ? `v${form.mobileAppVersionIos.trim().replace(/^v/i, '')}` : 'Not specified',
      mobileAppVersionAndroid: form.mobileAppVersionAndroid.trim() ? `v${form.mobileAppVersionAndroid.trim().replace(/^v/i, '')}` : 'Not specified',
    };
  }, [
    form.firmwareVersion,
    form.items,
    form.kickoffEmail,
    form.mobileAppVersion,
    form.mobileAppVersionAndroid,
    form.mobileAppVersionIos,
    form.name,
    form.overview,
    form.releaseNotes,
    program.track,
  ]);
  const effectiveKickoffEmailSubject = kickoffEmailSubject.trim() || kickoffEmailPreview.subject;
  const defaultKickoffAudienceLabel = program.track === 'internal' ? 'all internal testers' : 'all beta testers';
  const selectedTesterSet = useMemo(() => new Set(form.selectedTesterIds), [form.selectedTesterIds]);
  const usedTaskLibraryIds = useMemo(
    () => new Set(form.items.map((item) => item.libraryItemId).filter(Boolean) as string[]),
    [form.items]
  );
  const usedQuestionLibraryIds = useMemo(
    () => new Set(form.questions.map((question) => question.libraryItemId).filter(Boolean) as string[]),
    [form.questions]
  );
  const canEditAudience = form.status === 'planning' || form.status === 'testing';
  const selectedTesterCount = form.deployToAllTesters ? testers.length : form.selectedTesterIds.length;
  const audienceTesters = form.deployToAllTesters
    ? testers
    : testers.filter((tester) => selectedTesterSet.has(tester._id));
  const eligibleAudienceTesters = isInternalProgram
    ? audienceTesters.filter(testerMatchesActivePlatforms)
    : audienceTesters;
  const iosAudienceCount = audienceTesters.filter((tester) => testerHasPlatform(tester, 'ios')).length;
  const androidAudienceCount = audienceTesters.filter((tester) => testerHasPlatform(tester, 'android')).length;

  const setDeployToAllTesters = (deployToAllTesters: boolean) => {
    if (!deployToAllTesters) setShowTesters(true);
    setForm((prev) => ({
      ...prev,
      deployToAllTesters,
      selectedTesterIds: deployToAllTesters
        ? []
        : prev.selectedTesterIds.length
          ? prev.selectedTesterIds
          : testers.filter(testerMatchesActivePlatforms).map((tester) => tester._id),
    }));
  };

  const toggleSelectedTester = (testerId: string, selected: boolean) => {
    setForm((prev) => {
      const current = new Set(prev.selectedTesterIds);
      if (selected) {
        current.add(testerId);
      } else {
        current.delete(testerId);
      }
      return { ...prev, selectedTesterIds: Array.from(current) };
    });
  };

  const setMobileAppPlatformEnabled = (platform: 'ios' | 'android', enabled: boolean) => {
    setForm((prev) => {
      const next = enabled
        ? [...new Set([...prev.mobileAppPlatforms, platform])]
        : prev.mobileAppPlatforms.filter((entry) => entry !== platform);
      return {
        ...prev,
        mobileAppPlatforms: next.length ? next : prev.mobileAppPlatforms,
      };
    });
  };

  const handleFirmwareSelect = (value: string) => {
    if (!value) {
      setForm((prev) => ({ ...prev, firmwareReleaseId: '', firmwareVersion: '' }));
      return;
    }
    const option = firmwareOptions.find((entry) => entry._id === value || entry.version === value);
    setForm((prev) => ({
      ...prev,
      firmwareReleaseId: option?._id || '',
      firmwareVersion: option?.version || value.replace(/^v/i, ''),
    }));
  };

  const importReleaseNotes = () => {
    const notes = selectedFirmware?.releaseNotes
      ?.split('\n')
      .map(stripMarkdownListMarker)
      .filter(Boolean)
      .join('\n')
      .trim();
    if (!notes) {
      setFormError('No release notes found for the selected firmware version.');
      return;
    }
    setFormError(null);
    setForm((prev) => ({ ...prev, releaseNotes: notes }));
  };

  const updateReleaseNote = (index: number, value: string) => {
    setForm((prev) => {
      const notes = prev.releaseNotes.split('\n');
      notes[index] = value;
      return { ...prev, releaseNotes: notes.join('\n') };
    });
  };

  const addReleaseNote = () => {
    setForm((prev) => ({
      ...prev,
      releaseNotes: prev.releaseNotes ? `${prev.releaseNotes}\n` : '\n',
    }));
  };

  const removeReleaseNote = (index: number) => {
    setForm((prev) => {
      const notes = prev.releaseNotes.split('\n');
      const nextNotes = notes.length <= 1 ? [''] : notes.filter((_, i) => i !== index);
      return { ...prev, releaseNotes: nextNotes.join('\n') };
    });
  };

  const sendKickoffEmail = async () => {
    if (!editingTest) {
      setKickoffEmailStatus({ type: 'danger', message: 'Save this test before sending a kickoff email.' });
      return;
    }

    const recipients = parseEmailRecipients(kickoffEmailRecipients);

    setSendingKickoffEmail(true);
    setKickoffEmailStatus(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}${program.testsApi}/${editingTest._id}/send-kickoff-email`,
        {
          recipients,
          subject: effectiveKickoffEmailSubject,
          intro: form.kickoffEmail.trim(),
          testName: form.name.trim() || editingTest.name,
          firmwareVersion: form.firmwareVersion,
          mobileAppVersion: form.mobileAppVersion,
          mobileAppVersionIos: form.mobileAppVersionIos,
          mobileAppVersionAndroid: form.mobileAppVersionAndroid,
          releaseNotes: kickoffEmailPreview.releaseNotes,
          instructions: form.overview.trim(),
          tasks: kickoffEmailPreview.tasks,
          wikiUrl: kickoffEmailPreview.wikiUrl,
        },
        { withCredentials: true }
      );

      setKickoffEmailStatus({
        type: 'success',
        message: response.data?.message || `Sent ${recipients.length} kickoff email event(s) to Klaviyo.`,
      });
    } catch (err: any) {
      setKickoffEmailStatus({
        type: 'danger',
        message: err.response?.data?.message || 'Failed to send kickoff email.',
      });
    } finally {
      setSendingKickoffEmail(false);
    }
  };

  const updateItem = (index: number, patch: Partial<DeployTestItem>) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));
  };

  const addItem = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, emptyItem()] }));
  };

  const addTaskFromLibrary = () => {
    const selected = taskLibrary.find((item) => item._id === taskLibraryPickId);
    if (!selected || usedTaskLibraryIds.has(selected._id)) return;
    const nextItems: DeployTestItem[] = [...form.items, {
        libraryItemId: selected._id,
        text: selected.text || '',
        description: selected.description || '',
        status: 'testing',
      }];
    setForm((prev) => ({ ...prev, items: nextItems }));
    setTaskLibraryPickId('');
  };

  const saveTaskToLibrary = async (item: DeployTestItem, index: number) => {
    const text = item.text.trim();
    if (!text) {
      setFormError('Task title is required before saving to the library.');
      return;
    }

    const key = `task-${index}`;
    setSavingLibraryKey(key);
    setFormError(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}${program.testsApi}/library`,
        {
          itemType: 'task',
          text,
          description: (item.description || '').trim(),
        },
        { withCredentials: true }
      );
      const saved = response.data.item as DeployTestLibraryItem;
      setTaskLibrary((prev) => [saved, ...prev.filter((entry) => entry._id !== saved._id)]);
      const nextItems = form.items.map((entry, i) => (
        i === index ? { ...entry, libraryItemId: saved._id } : entry
      ));
      setForm((prev) => ({ ...prev, items: nextItems }));
      if (editingTest) {
        await axios.patch(
          `${API_BASE_URL}${program.testsApi}/${editingTest._id}`,
          {
            items: nextItems.map((entry) => ({
              _id: entry._id,
              libraryItemId: entry.libraryItemId || null,
              text: entry.text.trim(),
              description: (entry.description || '').trim(),
              status: resolveItemStatus(entry),
            })),
          },
          { withCredentials: true }
        );
      }
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to save task to library.');
    } finally {
      setSavingLibraryKey(null);
    }
  };

  const removeItem = (index: number) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.length === 1
        ? [emptyItem()]
        : prev.items.filter((_, i) => i !== index),
    }));
  };

  const updateQuestion = (index: number, patch: Partial<DeployTestQuestion>) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((question, i) => (i === index ? { ...question, ...patch } : question)),
    }));
  };

  const addQuestion = () => {
    setForm((prev) => ({ ...prev, questions: [...prev.questions, emptyQuestion()] }));
  };

  const addQuestionFromLibrary = () => {
    const selected = questionLibrary.find((item) => item._id === questionLibraryPickId);
    if (!selected || usedQuestionLibraryIds.has(selected._id)) return;
    const nextQuestions: DeployTestQuestion[] = [...form.questions, {
        libraryItemId: selected._id,
        prompt: selected.prompt || '',
        type: selected.type || 'text',
        choices: selected.type === 'choice'
          ? (selected.choices && selected.choices.length > 0 ? selected.choices : [''])
          : [],
        additionalInfo: selected.additionalInfo || '',
      }];
    setForm((prev) => ({ ...prev, questions: nextQuestions }));
    setQuestionLibraryPickId('');
  };

  const saveQuestionToLibrary = async (question: DeployTestQuestion, index: number) => {
    const prompt = question.prompt.trim();
    if (!prompt) {
      setFormError('Question prompt is required before saving to the library.');
      return;
    }

    const key = `question-${index}`;
    setSavingLibraryKey(key);
    setFormError(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}${program.testsApi}/library`,
        {
          itemType: 'question',
          prompt,
          type: question.type,
          additionalInfo: (question.additionalInfo || '').trim(),
          choices: question.type === 'choice'
            ? (question.choices || []).map((choice) => choice.trim()).filter(Boolean)
            : [],
        },
        { withCredentials: true }
      );
      const saved = response.data.item as DeployTestLibraryItem;
      setQuestionLibrary((prev) => [saved, ...prev.filter((entry) => entry._id !== saved._id)]);
      const nextQuestions = form.questions.map((entry, i) => (
        i === index ? { ...entry, libraryItemId: saved._id } : entry
      ));
      setForm((prev) => ({ ...prev, questions: nextQuestions }));
      if (editingTest) {
        await axios.patch(
          `${API_BASE_URL}${program.testsApi}/${editingTest._id}`,
          {
            questions: nextQuestions
              .map((entry) => ({
                _id: entry._id,
                libraryItemId: entry.libraryItemId || null,
                prompt: entry.prompt.trim(),
                type: entry.type,
                additionalInfo: (entry.additionalInfo || '').trim(),
                choices: entry.type === 'choice'
                  ? (entry.choices || []).map((choice) => choice.trim()).filter(Boolean)
                  : [],
              }))
              .filter((entry) => entry.prompt),
          },
          { withCredentials: true }
        );
      }
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to save question to library.');
    } finally {
      setSavingLibraryKey(null);
    }
  };

  const removeQuestion = (index: number) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };

  const updateQuestionChoice = (questionIndex: number, choiceIndex: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((question, i) => {
        if (i !== questionIndex) return question;
        const choices = [...(question.choices || [])];
        choices[choiceIndex] = value;
        return { ...question, choices };
      }),
    }));
  };

  const addQuestionChoice = (questionIndex: number) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((question, i) => (
        i === questionIndex
          ? { ...question, choices: [...(question.choices || []), ''] }
          : question
      )),
    }));
  };

  const removeQuestionChoice = (questionIndex: number, choiceIndex: number) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((question, i) => {
        if (i !== questionIndex) return question;
        const choices = (question.choices || []).filter((_, ci) => ci !== choiceIndex);
        return { ...question, choices: choices.length ? choices : [''] };
      }),
    }));
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    setForm((prev) => {
      if (nextIndex < 0 || nextIndex >= prev.items.length) return prev;
      const items = [...prev.items];
      const [moved] = items.splice(index, 1);
      items.splice(nextIndex, 0, moved);
      return { ...prev, items };
    });
  };

  const moveQuestion = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    setForm((prev) => {
      if (nextIndex < 0 || nextIndex >= prev.questions.length) return prev;
      const questions = [...prev.questions];
      const [moved] = questions.splice(index, 1);
      questions.splice(nextIndex, 0, moved);
      return { ...prev, questions };
    });
  };

  const buildPayload = (status: DeployTestStatus = form.status) => ({
    name: form.name.trim(),
    track: program.track,
    status,
    firmwareVersion: form.firmwareVersion.trim().replace(/^v/i, ''),
    firmwareReleaseId: form.firmwareReleaseId || null,
    mobileAppVersion: form.mobileAppVersionIos === form.mobileAppVersionAndroid
      ? form.mobileAppVersionIos.trim().replace(/^v/i, '')
      : '',
    mobileAppVersionIos: form.mobileAppVersionIos.trim().replace(/^v/i, ''),
    mobileAppVersionAndroid: form.mobileAppVersionAndroid.trim().replace(/^v/i, ''),
    mobileAppPlatforms: isInternalProgram ? form.mobileAppPlatforms : ['ios', 'android'],
    overview: form.overview,
    releaseNotes: form.releaseNotes,
    kickoffEmail: form.kickoffEmailEnabled ? form.kickoffEmail : '',
    deployToAllTesters: form.deployToAllTesters,
    selectedTesterIds: form.deployToAllTesters ? [] : form.selectedTesterIds,
    items: form.items
      .map((item) => ({
        _id: item._id,
        libraryItemId: item.libraryItemId || null,
        text: item.text.trim(),
        description: (item.description || '').trim(),
        status: resolveItemStatus(item),
      }))
      .filter((item) => item.text),
    questions: form.questions
      .map((question) => ({
        _id: question._id,
        libraryItemId: question.libraryItemId || null,
        prompt: question.prompt.trim(),
        type: question.type,
        additionalInfo: (question.additionalInfo || '').trim(),
        choices: question.type === 'choice'
          ? (question.choices || []).map((choice) => choice.trim()).filter(Boolean)
          : [],
      }))
      .filter((question) => question.prompt),
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEditAudience && form.status !== 'planning') {
      return;
    }
    const name = form.name.trim();
    if (!name) {
      setFormError('Name is required');
      return;
    }

    setSaving(true);
    setFormError(null);

    try {
      if (editingTest) {
        const response = await axios.patch(`${API_BASE_URL}${program.testsApi}/${editingTest._id}`, buildPayload(), {
          withCredentials: true,
        });
        const test = response.data.test as DeployTest;
        setEditingTest(test);
        setForm(formFromTest(test));
      } else {
        const response = await axios.post(`${API_BASE_URL}${program.testsApi}`, buildPayload(), { withCredentials: true });
        const test = response.data.test as DeployTest;
        navigate(program.testPath(test._id), { replace: true });
      }
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to save deploy test');
    } finally {
      setSaving(false);
    }
  };

  const handleAbort = async () => {
    if (!editingTest || editingTest.status !== 'testing') return;
    if (!window.confirm(`Abort test "${editingTest.name}"?`)) return;

    setAborting(true);
    setFormError(null);
    try {
      const response = await axios.patch(
        `${API_BASE_URL}${program.testsApi}/${editingTest._id}`,
        { status: 'aborted' },
        { withCredentials: true }
      );
      const test = response.data.test as DeployTest;
      setEditingTest(test);
      setForm(formFromTest(test));
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to abort deploy test');
    } finally {
      setAborting(false);
    }
  };

  const handleReinstate = async () => {
    if (!editingTest || form.status !== 'aborted') return;

    setReinstating(true);
    setFormError(null);
    try {
      const response = await axios.patch(
        `${API_BASE_URL}${program.testsApi}/${editingTest._id}`,
        buildPayload('planning'),
        { withCredentials: true }
      );
      const test = response.data.test as DeployTest;
      setEditingTest(test);
      setForm(formFromTest(test));
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to reinstate deploy test');
    } finally {
      setReinstating(false);
    }
  };

  const handleDelete = async () => {
    if (!editingTest) return;
    if (!window.confirm(`Delete test "${editingTest.name}"?`)) return;

    setDeleting(true);
    setFormError(null);
    try {
      await axios.delete(`${API_BASE_URL}${program.testsApi}/${editingTest._id}`, {
        withCredentials: true,
      });
      navigate(program.listPath);
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to delete deploy test');
      setDeleting(false);
    }
  };

  const pageTitle = isNew
    ? 'New Test'
    : form.status === 'planning'
      ? 'Edit Test'
      : 'View Test';
  const headerTitle = isNew
    ? pageTitle
    : `${pageTitle}: "${(form.name || editingTest?.name || '').trim() || 'Untitled'}"`;

  const testersSection = (
    <section className="border rounded p-3 mb-4 bg-white">
      <h5 className="mb-3">Testers</h5>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input deploy-test-green-switch"
              type="checkbox"
              id="deploy-test-all-testers"
              checked={form.deployToAllTesters}
              onChange={(e) => setDeployToAllTesters(e.target.checked)}
              disabled={!canEditAudience}
            />
            <label className="form-check-label fw-bold" htmlFor="deploy-test-all-testers">
              Deploy to all testers
            </label>
          </div>
          <div className="border rounded-3 bg-light px-3 py-2 small">
            <div className="fw-bold text-dark">
              {isInternalProgram
                ? `${eligibleAudienceTesters.length.toLocaleString()} eligible tester${eligibleAudienceTesters.length === 1 ? '' : 's'}`
                : form.deployToAllTesters
                  ? `${testers.length.toLocaleString()} testers`
                  : `${selectedTesterCount.toLocaleString()} of ${testers.length.toLocaleString()} testers selected`}
            </div>
            {isInternalProgram && (
              <div className="d-flex flex-wrap gap-2 mt-1">
                <span className={iosEnabled ? 'text-muted' : 'text-muted opacity-50'}>
                  iOS: {iosAudienceCount.toLocaleString()}
                </span>
                <span className={androidEnabled ? 'text-muted' : 'text-muted opacity-50'}>
                  Android: {androidAudienceCount.toLocaleString()}
                </span>
                {!form.deployToAllTesters && (
                  <span className="text-muted">
                    Selected: {selectedTesterCount.toLocaleString()} / {testers.length.toLocaleString()}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="form-text">
          Only selected testers can receive the kickoff email, see this test in the app, or access it from the testing portal.
        </div>

        <button
          type="button"
          className={`deploy-test-show-testers${showTesters ? ' is-open' : ''}`}
          aria-expanded={showTesters}
          onClick={() => setShowTesters((open) => !open)}
        >
          <span>{showTesters ? 'Hide testers' : 'Show testers'}</span>
          <FontAwesomeIcon icon={faChevronDown} />
        </button>

        {showTesters && (
          <div className="deploy-test-testers-panel">
            {testersLoading ? (
              <div className="d-flex align-items-center gap-2 text-muted small">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                Loading testers...
              </div>
            ) : testers.length === 0 ? (
              <div className="text-muted small">No testers found.</div>
            ) : (
              <div className="table-responsive" style={{ maxHeight: 360, overflowY: 'auto' }}>
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 120 }}>Included</th>
                      <th>Name</th>
                      <th>Email</th>
                      {isInternalProgram && <th className="text-center" style={{ width: 90 }}>iOS</th>}
                      {isInternalProgram && <th className="text-center" style={{ width: 90 }}>Android</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {testers.map((tester) => {
                      const hasIos = testerHasPlatform(tester, 'ios');
                      const hasAndroid = testerHasPlatform(tester, 'android');
                      const eligible = testerMatchesActivePlatforms(tester);
                      const checked = form.deployToAllTesters ? eligible : selectedTesterSet.has(tester._id);
                      const name = `${tester.firstName || ''} ${tester.lastName || ''}`.trim() || '—';
                      return (
                        <tr key={tester._id} className={isInternalProgram && !eligible ? 'text-muted opacity-50' : undefined}>
                          <td>
                            <div className="form-check form-switch mb-0">
                              <input
                                className="form-check-input deploy-test-green-switch"
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => toggleSelectedTester(tester._id, e.target.checked)}
                                disabled={
                                  form.deployToAllTesters
                                  || !canEditAudience
                                  || (isInternalProgram && !eligible)
                                }
                                aria-label={`${checked ? 'Remove' : 'Add'} ${tester.email || name}`}
                              />
                            </div>
                          </td>
                          <td>{name}</td>
                          <td className="text-muted">{tester.email}</td>
                          {isInternalProgram && (
                            <td className={`text-center${iosEnabled ? '' : ' text-muted opacity-50'}`}>
                              {hasIos ? <span className="text-success fw-bold">✓</span> : <span className="text-muted">—</span>}
                            </td>
                          )}
                          {isInternalProgram && (
                            <td className={`text-center${androidEnabled ? '' : ' text-muted opacity-50'}`}>
                              {hasAndroid ? <span className="text-success fw-bold">✓</span> : <span className="text-muted">—</span>}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
    </section>
  );

  return (
    <div className="container-fluid mt-3 mb-5">
      <div className="mb-3">
        <Link to={program.listPath} className="btn btn-sm btn-outline-secondary">
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back
        </Link>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '180px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <form onSubmit={handleSave}>
          <div className="d-flex align-items-center gap-2 mb-3">
            <FontAwesomeIcon icon={faListCheck} className={`${program.iconClass} fs-2`} />
            <h2 className="mb-0">{headerTitle}</h2>
          </div>

          <div>
            <div>
              {formError && <div className="alert alert-danger">{formError}</div>}
              {form.status !== 'planning' && (
                <div className="alert alert-secondary py-2">
                  {form.status === 'aborted'
                    ? 'This test was aborted. Update the firmware or mobile app version, then reinstate it to return to Planning.'
                    : form.status === 'testing'
                      ? 'This test has started. Testers and task status can still be updated.'
                      : 'This test can no longer be edited because it has ended.'}
                </div>
              )}

              <section className="border rounded p-3 mb-4 bg-white">
                <h5 className="mb-3">Test Details</h5>

              {editingTest && (
                <div className="mb-3">
                  <label className="form-label fw-bold">Public page</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control bg-light text-muted"
                      value={program.publicTestHref({
                        slug: editingTest.slug,
                        name: form.name || editingTest.name,
                      })}
                      readOnly
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={async () => {
                        const url = program.publicTestHref({
                          slug: editingTest.slug,
                          name: form.name || editingTest.name,
                        });
                        try {
                          await navigator.clipboard.writeText(url);
                          setCopiedPublicUrl(true);
                          window.setTimeout(() => setCopiedPublicUrl(false), 1500);
                        } catch {
                          window.prompt('Copy public URL', url);
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faCopy} className="me-1" />
                      {copiedPublicUrl ? 'Copied' : 'Copy'}
                    </button>
                    <a
                      href={program.publicTestHref({
                        slug: editingTest.slug,
                        name: form.name || editingTest.name,
                      })}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-secondary"
                    >
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="me-1" />
                      Open
                    </a>
                  </div>
                </div>
              )}

              <div className="mb-3">
                <label htmlFor="deploy-test-name" className="form-label fw-bold">Name</label>
                <input
                  id="deploy-test-name"
                  type="text"
                  className="form-control"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  required
                  disabled={form.status !== 'planning'}
                />
              </div>

              <div className="row">
                <div className="col-md-3 mb-3">
                  <label htmlFor="deploy-test-status" className="form-label fw-bold">Status</label>
                  <select
                    id="deploy-test-status"
                    className="form-select"
                    value={form.status}
                    onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value as DeployTestStatus }))}
                    disabled
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 mb-3">
                  <label htmlFor="deploy-test-firmware" className="form-label fw-bold">
                    Firmware Version
                    {selectedFirmware?.type && (
                      <span className={`deploy-test-fw-badge is-${selectedFirmware.type} ms-2`}>
                        {firmwareTypeLabel(selectedFirmware.type)}
                      </span>
                    )}
                  </label>
                  <select
                    id="deploy-test-firmware"
                    className="form-select"
                    value={form.firmwareReleaseId || form.firmwareVersion}
                    onChange={(e) => handleFirmwareSelect(e.target.value)}
                    disabled={!canEditVersions}
                  >
                    <option value={SELECT_LATER}>Select Later</option>
                    {uniqueFirmwareVersions.map((option) => (
                      <option key={option._id} value={option._id}>
                        v{option.version}{option.type ? ` (${option.type})` : ''}
                      </option>
                    ))}
                    {form.firmwareVersion
                      && !uniqueFirmwareVersions.some((option) => option.version === form.firmwareVersion)
                      && (
                        <option value={form.firmwareVersion}>
                          v{form.firmwareVersion}
                        </option>
                      )}
                  </select>
                  <div className="form-text">
                    Alpha and Dev can be selected for planning. The test can start once this version is promoted to Beta.
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className={`rounded-3 border p-3 h-100${iosEnabled ? ' bg-white' : ' bg-light text-muted'}`}>
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                      <label htmlFor="deploy-test-app-ios" className="form-label fw-bold mb-0">Mobile App (iOS)</label>
                      {isInternalProgram && (
                        <div className="form-check form-switch m-0">
                          <input
                            id="deploy-test-platform-ios"
                            className="form-check-input"
                            type="checkbox"
                            checked={iosEnabled}
                            onChange={(event) => setMobileAppPlatformEnabled('ios', event.target.checked)}
                            disabled={!canEditVersions}
                            aria-label="Include iOS"
                          />
                        </div>
                      )}
                    </div>
                    <select
                      id="deploy-test-app-ios"
                      className="form-select"
                      value={form.mobileAppVersionIos}
                      onChange={(e) => setForm((prev) => ({ ...prev, mobileAppVersionIos: e.target.value }))}
                      disabled={!canEditVersions || !iosEnabled}
                    >
                      <option value={SELECT_LATER}>Select Later</option>
                      {uniqueMobileAppVersionsByPlatform.ios.map((option) => (
                        <option key={`ios-${option.version}`} value={option.version}>
                          v{option.version}{option.channel ? ` (${option.channel})` : ''}
                        </option>
                      ))}
                      {form.mobileAppVersionIos
                        && !uniqueMobileAppVersionsByPlatform.ios.some((option) => option.version === form.mobileAppVersionIos)
                        && (
                          <option value={form.mobileAppVersionIos}>
                            v{form.mobileAppVersionIos}
                          </option>
                        )}
                    </select>
                    {isInternalProgram && (
                      <div className="form-text">
                        {iosEnabled ? 'iOS testers can receive this test.' : 'iOS testers will not receive this test.'}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className={`rounded-3 border p-3 h-100${androidEnabled ? ' bg-white' : ' bg-light text-muted'}`}>
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                      <label htmlFor="deploy-test-app-android" className="form-label fw-bold mb-0">Mobile App (Android)</label>
                      {isInternalProgram && (
                        <div className="form-check form-switch m-0">
                          <input
                            id="deploy-test-platform-android"
                            className="form-check-input"
                            type="checkbox"
                            checked={androidEnabled}
                            onChange={(event) => setMobileAppPlatformEnabled('android', event.target.checked)}
                            disabled={!canEditVersions}
                            aria-label="Include Android"
                          />
                        </div>
                      )}
                    </div>
                    <select
                      id="deploy-test-app-android"
                      className="form-select"
                      value={form.mobileAppVersionAndroid}
                      onChange={(e) => setForm((prev) => ({ ...prev, mobileAppVersionAndroid: e.target.value }))}
                      disabled={!canEditVersions || !androidEnabled}
                    >
                      <option value={SELECT_LATER}>Select Later</option>
                      {uniqueMobileAppVersionsByPlatform.android.map((option) => (
                        <option key={`android-${option.version}`} value={option.version}>
                          v{option.version}{option.channel ? ` (${option.channel})` : ''}
                        </option>
                      ))}
                      {form.mobileAppVersionAndroid
                        && !uniqueMobileAppVersionsByPlatform.android.some((option) => option.version === form.mobileAppVersionAndroid)
                        && (
                          <option value={form.mobileAppVersionAndroid}>
                            v{form.mobileAppVersionAndroid}
                          </option>
                        )}
                    </select>
                    {isInternalProgram && (
                      <div className="form-text">
                        {androidEnabled ? 'Android testers can receive this test.' : 'Android testers will not receive this test.'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              </section>

              {testersSection}

              <section className="border rounded p-3 mb-4 bg-white">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="mb-0">Release Notes</h5>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={importReleaseNotes}
                      disabled={!selectedFirmware || !canImportReleaseNotes}
                    >
                      <FontAwesomeIcon icon={faFileImport} className="me-1" />
                      Import notes
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={addReleaseNote}
                      disabled={form.status !== 'planning'}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-1" />
                      Add note
                    </button>
                  </div>
                </div>
                {releaseNoteLines.map((note, index) => (
                  <div key={`release-note-${index}`} className="d-flex gap-2 mb-2">
                    <input
                      type="text"
                      className="form-control"
                      value={note}
                      onChange={(e) => updateReleaseNote(index, e.target.value)}
                      placeholder={`Release note ${index + 1}`}
                      disabled={form.status !== 'planning'}
                      aria-label={`Release note ${index + 1}`}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => removeReleaseNote(index)}
                      disabled={form.status !== 'planning'}
                      aria-label="Remove release note"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                ))}
              </section>

              <section className="border rounded p-3 mb-4 bg-white">
              <h5 className="mb-3">Instructions</h5>
              <div className="form-label fw-bold mb-2">Kickoff Email</div>
              <div className="deploy-test-kickoff-section mb-3">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="deploy-test-kickoff-enabled"
                      checked={form.kickoffEmailEnabled}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        kickoffEmailEnabled: e.target.checked,
                      }))}
                      disabled={form.status !== 'planning'}
                    />
                    <label className="form-check-label fw-bold" htmlFor="deploy-test-kickoff-enabled">
                      Include kickoff email
                    </label>
                  </div>
                </div>

                {form.kickoffEmailEnabled && (
                  <>
                    <MarkdownHelp htmlFor="deploy-test-kickoff-email">Email Intro</MarkdownHelp>
                    <textarea
                      id="deploy-test-kickoff-email"
                      className="form-control"
                      rows={6}
                      value={form.kickoffEmail}
                      onChange={(e) => setForm((prev) => ({ ...prev, kickoffEmail: e.target.value }))}
                      placeholder="Short intro inserted after “Hello testers!”"
                      disabled={form.status !== 'planning'}
                    />
                    <label htmlFor="deploy-test-kickoff-subject" className="form-label fw-bold mt-3">Subject</label>
                    <input
                      id="deploy-test-kickoff-subject"
                      type="text"
                      className="form-control"
                      value={kickoffEmailSubject}
                      onChange={(e) => setKickoffEmailSubject(e.target.value)}
                      placeholder={kickoffEmailPreview.subject}
                    />
                    <div className="form-text">
                      Leave blank to use the generated subject.
                    </div>
                    <label htmlFor="deploy-test-kickoff-recipients" className="form-label fw-bold mt-3">Recipients</label>
                    <textarea
                      id="deploy-test-kickoff-recipients"
                      className="form-control"
                      rows={2}
                      value={kickoffEmailRecipients}
                      onChange={(e) => setKickoffEmailRecipients(e.target.value)}
                      placeholder="tester@example.com, another@example.com"
                    />
                    <div className="form-text">
                      Leave blank to send to {form.deployToAllTesters
                        ? defaultKickoffAudienceLabel
                        : `${selectedTesterCount.toLocaleString()} selected tester${selectedTesterCount === 1 ? '' : 's'}`}.
                      Enter emails to override within the selected audience.
                    </div>
                    {!editingTest && (
                      <div className="text-muted small mt-2">
                        Save this test before sending the kickoff email.
                      </div>
                    )}
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setPreviewEmailOpen(true)}
                      >
                        Preview email
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={sendKickoffEmail}
                        disabled={!editingTest || sendingKickoffEmail}
                      >
                        {!sendingKickoffEmail && <FontAwesomeIcon icon={faPaperPlane} className="me-1" />}
                        {sendingKickoffEmail ? 'Sending...' : 'Send kickoff email'}
                      </button>
                    </div>
                    {kickoffEmailStatus && (
                      <div className={`alert alert-${kickoffEmailStatus.type} py-2 mt-3 mb-0`}>
                        {kickoffEmailStatus.message}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="mb-3">
                <MarkdownHelp htmlFor="deploy-test-overview">Instructions</MarkdownHelp>
                <textarea
                  id="deploy-test-overview"
                  className="form-control"
                  rows={4}
                  value={form.overview}
                  onChange={(e) => setForm((prev) => ({ ...prev, overview: e.target.value }))}
                  placeholder="Instructions for testers"
                  disabled={form.status !== 'planning'}
                />
              </div>
              </section>

              <section className="border rounded p-3 mb-4 bg-white">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="mb-0">Tasks</h5>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                  <select
                    className="form-select"
                    style={{ maxWidth: '420px' }}
                    value={taskLibraryPickId}
                    onChange={(e) => setTaskLibraryPickId(e.target.value)}
                    disabled={form.status !== 'planning'}
                    aria-label="Add existing library task"
                  >
                    <option value="">Add existing task from library...</option>
                    {taskLibrary.map((item) => {
                      const alreadyAdded = usedTaskLibraryIds.has(item._id);
                      return (
                        <option key={item._id} value={item._id} disabled={alreadyAdded}>
                          {item.text || 'Untitled task'}{alreadyAdded ? ' (added)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={addTaskFromLibrary}
                    disabled={form.status !== 'planning' || !taskLibraryPickId || usedTaskLibraryIds.has(taskLibraryPickId)}
                  >
                    Add from library
                  </button>
                </div>
                {form.status !== 'testing' && (
                  <div className="text-muted small mb-2">
                    Task status can only be changed while the test is in Testing.
                  </div>
                )}
                {form.items.map((item, index) => (
                  <div key={item._id || index} className="deploy-test-task-editor">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      {form.status === 'planning' || form.status === 'aborted' ? (
                        <span className={`deploy-test-card-task-status is-${form.status}`}>
                          {form.status === 'aborted' ? 'Aborted' : 'Planning'}
                        </span>
                      ) : (
                        <select
                          className="form-select flex-shrink-0"
                          style={{ width: '120px' }}
                          value={resolveItemStatus(item)}
                          disabled={form.status !== 'testing'}
                          onChange={(e) => updateItem(index, { status: e.target.value as DeployTestItemStatus })}
                          aria-label={`Task ${index + 1} status`}
                        >
                          {ITEM_STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      )}
                      <input
                        type="text"
                        className="form-control"
                        value={item.text}
                        onChange={(e) => updateItem(index, { text: e.target.value })}
                        placeholder={`Title ${index + 1}`}
                        disabled={form.status !== 'planning' || Boolean(item.libraryItemId)}
                        aria-label={`Task ${index + 1} title`}
                      />
                      {item.libraryItemId ? (
                        <span className="badge text-bg-secondary flex-shrink-0">
                          <FontAwesomeIcon icon={faBookmark} className="me-1" />
                          Library
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm flex-shrink-0"
                          onClick={() => saveTaskToLibrary(item, index)}
                          disabled={savingLibraryKey === `task-${index}` || !item.text.trim()}
                          title="Add task to library"
                          aria-label="Add task to library"
                        >
                          {savingLibraryKey === `task-${index}` ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faBookmark} className="me-1" />
                              Add To Library
                            </>
                          )}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => moveItem(index, -1)}
                        disabled={index === 0 || form.status !== 'planning'}
                        aria-label="Move up"
                      >
                        <FontAwesomeIcon icon={faArrowUp} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => moveItem(index, 1)}
                        disabled={index === form.items.length - 1 || form.status !== 'planning'}
                        aria-label="Move down"
                      >
                        <FontAwesomeIcon icon={faArrowDown} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeItem(index)}
                        disabled={form.status !== 'planning'}
                        aria-label="Remove item"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                    <MarkdownHelp htmlFor={`deploy-test-task-description-${index}`}>Description</MarkdownHelp>
                    <textarea
                      id={`deploy-test-task-description-${index}`}
                      className="form-control"
                      rows={3}
                      value={item.description || ''}
                      onChange={(e) => updateItem(index, { description: e.target.value })}
                      placeholder="Description"
                      disabled={form.status !== 'planning' || Boolean(item.libraryItemId)}
                      aria-label={`Task ${index + 1} description`}
                    />
                  </div>
                ))}
                <div className="text-end">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={addItem}
                    disabled={form.status !== 'planning'}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Add Task
                  </button>
                </div>
              </section>

              <section className="border rounded p-3 mb-4 bg-white">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="mb-0">Questions</h5>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                  <select
                    className="form-select"
                    style={{ maxWidth: '420px' }}
                    value={questionLibraryPickId}
                    onChange={(e) => setQuestionLibraryPickId(e.target.value)}
                    disabled={form.status !== 'planning'}
                    aria-label="Add existing library question"
                  >
                    <option value="">Add existing question from library...</option>
                    {questionLibrary.map((item) => {
                      const alreadyAdded = usedQuestionLibraryIds.has(item._id);
                      return (
                        <option key={item._id} value={item._id} disabled={alreadyAdded}>
                          {item.prompt || 'Untitled question'}{alreadyAdded ? ' (added)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={addQuestionFromLibrary}
                    disabled={form.status !== 'planning' || !questionLibraryPickId || usedQuestionLibraryIds.has(questionLibraryPickId)}
                  >
                    Add from library
                  </button>
                </div>
                {form.questions.length === 0 && (
                  <div className="text-muted small mb-2">No questions yet.</div>
                )}
                {form.questions.map((question, index) => (
                  <div key={question._id || `question-${index}`} className="deploy-test-task-editor">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        value={question.prompt}
                        onChange={(e) => updateQuestion(index, { prompt: e.target.value })}
                        placeholder={`Question ${index + 1}`}
                        disabled={form.status !== 'planning' || Boolean(question.libraryItemId)}
                        aria-label={`Question ${index + 1} prompt`}
                      />
                      <select
                        className="form-select flex-shrink-0"
                        style={{ width: '180px' }}
                        value={question.type}
                        disabled={form.status !== 'planning' || Boolean(question.libraryItemId)}
                        onChange={(e) => {
                          const type = e.target.value as DeployTestQuestionType;
                          updateQuestion(index, {
                            type,
                            choices: type === 'choice'
                              ? (question.choices && question.choices.length > 0 ? question.choices : ['', ''])
                              : [],
                          });
                        }}
                        aria-label={`Question ${index + 1} type`}
                      >
                        {QUESTION_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      {question.libraryItemId ? (
                        <span className="badge text-bg-secondary flex-shrink-0">
                          <FontAwesomeIcon icon={faBookmark} className="me-1" />
                          Library
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm flex-shrink-0"
                          onClick={() => saveQuestionToLibrary(question, index)}
                          disabled={savingLibraryKey === `question-${index}` || !question.prompt.trim()}
                          title="Add question to library"
                          aria-label="Add question to library"
                        >
                          {savingLibraryKey === `question-${index}` ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faBookmark} className="me-1" />
                              Add To Library
                            </>
                          )}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => moveQuestion(index, -1)}
                        disabled={index === 0 || form.status !== 'planning'}
                        aria-label="Move question up"
                      >
                        <FontAwesomeIcon icon={faArrowUp} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => moveQuestion(index, 1)}
                        disabled={index === form.questions.length - 1 || form.status !== 'planning'}
                        aria-label="Move question down"
                      >
                        <FontAwesomeIcon icon={faArrowDown} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeQuestion(index)}
                        disabled={form.status !== 'planning'}
                        aria-label="Remove question"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                    {question.type === 'choice' && (
                      <div className="mb-2">
                        <label className="form-label fw-bold">Choices</label>
                        {(question.choices || []).map((choice, choiceIndex) => (
                          <div key={`${question._id || index}-choice-${choiceIndex}`} className="d-flex gap-2 mb-2">
                            <input
                              type="text"
                              className="form-control"
                              value={choice}
                              onChange={(e) => updateQuestionChoice(index, choiceIndex, e.target.value)}
                              placeholder={`Choice ${choiceIndex + 1}`}
                              disabled={form.status !== 'planning' || Boolean(question.libraryItemId)}
                            />
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => removeQuestionChoice(index, choiceIndex)}
                              disabled={form.status !== 'planning' || Boolean(question.libraryItemId) || (question.choices || []).length <= 1}
                              aria-label="Remove choice"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => addQuestionChoice(index)}
                          disabled={form.status !== 'planning' || Boolean(question.libraryItemId)}
                        >
                          <FontAwesomeIcon icon={faPlus} className="me-1" />
                          Add choice
                        </button>
                      </div>
                    )}
                    {question.type !== 'text' && (
                      <div>
                        <label className="form-label fw-bold">Additional Info</label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={question.additionalInfo || ''}
                          onChange={(e) => updateQuestion(index, { additionalInfo: e.target.value })}
                          placeholder="Optional extra prompt shown with this question"
                          disabled={form.status !== 'planning' || Boolean(question.libraryItemId)}
                        />
                      </div>
                    )}
                  </div>
                ))}
                <div className="text-end">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={addQuestion}
                    disabled={form.status !== 'planning'}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Add Question
                  </button>
                </div>
              </section>
            </div>
            <div className="d-flex justify-content-between mt-3">
              <div className="d-flex gap-2">
                {editingTest && form.status === 'planning' && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDelete}
                    disabled={saving || deleting || aborting || reinstating}
                  >
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                )}
                {editingTest && form.status === 'testing' && (
                  <button
                    type="button"
                    className="btn btn-outline-warning"
                    onClick={handleAbort}
                    disabled={saving || deleting || aborting || reinstating}
                  >
                    <FontAwesomeIcon icon={faBan} className="me-1" />
                    {aborting ? 'Aborting…' : 'Abort'}
                  </button>
                )}
                {editingTest && form.status === 'aborted' && (
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={handleReinstate}
                    disabled={saving || deleting || aborting || reinstating}
                  >
                    {reinstating ? 'Reinstating…' : 'Reinstate'}
                  </button>
                )}
              </div>
              <div className="d-flex gap-2">
                <Link
                  to={program.listPath}
                  className={`btn btn-outline-secondary${saving || deleting || aborting || reinstating ? ' disabled' : ''}`}
                >
                  {form.status === 'planning' ? 'Cancel' : 'Close'}
                </Link>
                {(form.status === 'planning' || form.status === 'testing') && (
                  <button type="submit" className="btn btn-primary" disabled={saving || deleting || aborting || reinstating}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {previewEmailOpen && (
            <div className="beta-leave-modal" role="dialog" aria-modal="true" aria-labelledby="kickoff-email-preview-title">
              <div className="beta-leave-modal-card beta-join-modal-card">
                <h2 id="kickoff-email-preview-title">Kickoff Email Preview</h2>
                <div className="kickoff-email-preview mt-3">
                  <p>
                    <strong>Subject:</strong>{' '}
                    {kickoffEmailSubject.trim()
                      ? effectiveKickoffEmailSubject
                      : (
                        <>
                          {kickoffEmailPreview.subjectPrefix} (<em>{kickoffEmailPreview.testName}</em>)
                        </>
                      )}
                  </p>
                  <hr />
                  <p>Hello testers!</p>
                  <div
                    className="kickoff-email-preview-html"
                    dangerouslySetInnerHTML={{ __html: markdownToHtml(kickoffEmailPreview.body) }}
                  />

                  <p className="kickoff-email-preview-meta">
                    <span><strong>Test Name:</strong> {kickoffEmailPreview.testName}</span>
                    <span><strong>Firmware Version:</strong> {kickoffEmailPreview.firmwareVersion}</span>
                    <span><strong>Mobile App (iOS):</strong> {kickoffEmailPreview.mobileAppVersionIos}</span>
                    <span><strong>Mobile App (Android):</strong> {kickoffEmailPreview.mobileAppVersionAndroid}</span>
                  </p>

                  <p className="kickoff-email-preview-heading"><strong>Release Notes</strong></p>
                  {kickoffEmailPreview.releaseNotes.length ? (
                    <ul>
                      {kickoffEmailPreview.releaseNotes.map((note, index) => (
                        <li key={`${note}-${index}`}>{note}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>[Release notes]</p>
                  )}

                  <p className="kickoff-email-preview-heading"><strong>Instructions</strong></p>
                  <div
                    className="kickoff-email-preview-html"
                    dangerouslySetInnerHTML={{ __html: markdownToHtml(kickoffEmailPreview.instructions) }}
                  />

                  <p className="kickoff-email-preview-heading"><strong>Tasks</strong></p>
                  {kickoffEmailPreview.tasks.length ? (
                    <ol>
                      {kickoffEmailPreview.tasks.map((task, index) => (
                        <li key={`${task.title}-${index}`}>
                          <strong>{task.title}</strong>
                          {task.description && (
                            <div className="mt-1">{task.description}</div>
                          )}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p>[Tasks]</p>
                  )}

                  <p className="kickoff-email-preview-heading"><strong>Getting Started</strong></p>
                  <p>
                    Confirm you are running the correct versions listed above. Make sure you are logged in on the app.
                    Then either follow the link in the app to the test or follow the link here.
                  </p>

                  <p className="kickoff-email-preview-heading"><strong>Need Help?</strong></p>
                  <p>
                    Check out the{' '}
                    <a href={kickoffEmailPreview.wikiUrl} target="_blank" rel="noopener noreferrer">wiki</a>.
                  </p>

                  <p className="mb-0">
                    Happy testing,<br />
                    <span className="kickoff-email-preview-signature">~the Fluora team~</span>
                  </p>
                </div>
                <div className="beta-leave-modal-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setPreviewEmailOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default DeployTestPage;
